import api from '@/helpers/api'

const API_SETTINGS = `/api/promotion-settings`
const API_ITEMS = `/api/promotion-items`

export default {
    namespaced: true,
    state: {
        status: '',
        settings: [],
        currentSetting: null,
        items: [],
        availableItems: [],
        errMessage: '',
        meta: {
            page: 1,
            limit: 10,
            total: 1
        }
    },
    mutations: {
        request(state) {
            state.status = 'loading'
        },
        success(state) {
            state.status = 'success'
        },
        error(state, { message }) {
            state.status = 'error'
            state.errMessage = message
        },
        setSettings(state, settings) {
            state.settings = settings
        },
        setCurrentSetting(state, setting) {
            state.currentSetting = setting
        },
        setItems(state, items) {
            state.items = items
        },
        setAvailableItems(state, items) {
            state.availableItems = items
        },
        clearCurrentSetting(state) {
            state.currentSetting = null
            state.items = []
            state.availableItems = []
        }
    },
    actions: {
        /**
         * Fetch all promotion settings for current corporate
         */
        readSettings({ commit }, params = {}) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('get', API_SETTINGS, params)
                    .then((result) => {
                        commit('success')
                        commit('setSettings', result.data.data)
                        return resolve(result.data.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Fetch single promotion setting by ID
         */
        readSingleSetting({ commit }, id) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('get', `${API_SETTINGS}/${id}`)
                    .then((result) => {
                        commit('success')
                        commit('setCurrentSetting', result.data.data)
                        return resolve(result.data.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Create new promotion setting
         */
        createSetting({ commit }, data) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('post', API_SETTINGS, {}, data)
                    .then((result) => {
                        commit('success')
                        commit('setCurrentSetting', result.data.data)
                        return resolve(result.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Update existing promotion setting
         */
        updateSetting({ commit }, { id, data }) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('put', `${API_SETTINGS}/${id}`, {}, data)
                    .then((result) => {
                        commit('success')
                        commit('setCurrentSetting', result.data.data)
                        return resolve(result.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Delete promotion setting
         */
        deleteSetting({ commit }, id) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('delete', `${API_SETTINGS}/${id}`)
                    .then((result) => {
                        commit('success')
                        commit('clearCurrentSetting')
                        return resolve(result.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Fetch promotion items for a setting
         */
        readItems({ commit }, promotionSettingId) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('get', `${API_ITEMS}/${promotionSettingId}`)
                    .then((result) => {
                        commit('success')
                        commit('setItems', result.data.data)
                        return resolve(result.data.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Fetch available items (vouchers/benefits) to add
         */
        readAvailableItems({ commit }, promotionSettingId) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('get', `${API_ITEMS}/${promotionSettingId}/available`)
                    .then((result) => {
                        commit('success')

                        // Process the API response to normalize vouchers and benefits
                        const responseData = result.data.data
                        const normalizedItems = []

                        // Add vouchers with normalized structure
                        if (responseData.vouchers && Array.isArray(responseData.vouchers)) {
                            responseData.vouchers.forEach(voucher => {
                                normalizedItems.push({
                                    id: voucher.id,
                                    name: voucher.name,
                                    type: 'voucher',
                                    item_type: 'voucher',
                                    description: voucher.desc || '',
                                    merchant: voucher.merchant,
                                    merchant_name: voucher.merchant?.name || '',
                                    point: voucher.point,
                                    stock: voucher.stock,
                                    expiration: voucher.expiration,
                                    avatar: voucher.avatar,
                                    is_limited: voucher.is_limited,
                                    cancelable: voucher.cancelable,
                                    member_tier_id: voucher.member_tier_id
                                })
                            })
                        }

                        // Add benefits with normalized structure
                        if (responseData.benefits && Array.isArray(responseData.benefits)) {
                            responseData.benefits.forEach(benefit => {
                                normalizedItems.push({
                                    id: benefit.id,
                                    name: benefit.name,
                                    type: 'benefit',
                                    item_type: 'benefit',
                                    description: benefit.description || '',
                                    merchant: benefit.merchant,
                                    merchant_name: benefit.merchant?.name || ''
                                })
                            })
                        }

                        commit('setAvailableItems', normalizedItems)
                        return resolve(normalizedItems)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Add items to promotion
         */
        addItems({ commit, dispatch }, { promotionSettingId, items }) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('post', API_ITEMS, {}, {
                    promotion_setting_id: promotionSettingId,
                    items: items
                })
                    .then((result) => {
                        commit('success')
                        dispatch('readItems', promotionSettingId)
                        dispatch('readAvailableItems', promotionSettingId)
                        return resolve(result.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Update items order
         */
        updateItemsOrder({ commit }, items) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('put', `${API_ITEMS}/order`, {}, { items })
                    .then((result) => {
                        commit('success')
                        return resolve(result.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        },

        /**
         * Remove item from promotion
         */
        removeItem({ commit, dispatch }, { itemId, promotionSettingId }) {
            commit('request')
            return new Promise((resolve, reject) => {
                api.request('delete', `${API_ITEMS}/${itemId}`)
                    .then((result) => {
                        commit('success')
                        dispatch('readItems', promotionSettingId)
                        dispatch('readAvailableItems', promotionSettingId)
                        return resolve(result.data)
                    })
                    .catch((err) => {
                        commit('error', { message: err })
                        return reject(err)
                    })
            })
        }
    },
    getters: {
        isLoading: (state) => state.status === 'loading',
        hasSettings: (state) => state.settings.length > 0,
        currentSetting: (state) => state.currentSetting,
        items: (state) => state.items,
        availableItems: (state) => state.availableItems
    }
}
